import { useEffect, useId, useRef } from "react";
import { m, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

let modalStackCount = 0;

export function ModalShell({
  title,
  children,
  onClose,
  maxWidthClassName = "max-w-3xl",
}: {
  title: React.ReactNode;
  children: React.ReactNode;
  onClose: () => void;
  /** Overrides the desktop max-width (default "max-w-3xl") — e.g. "max-w-5xl"
   * for content that needs more room, like a wide table. */
  maxWidthClassName?: string;
}) {
  const { t } = useTranslation("common");
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  /* -------------------------------- */
  /* ESC KEY SUPPORT */
  /* -------------------------------- */
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      // A dialog stacked on top (e.g. Quick View) marks the key press it
      // handled, so one Escape closes one layer rather than all of them.
      if (e.key === "Escape" && !e.defaultPrevented) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  /* -------------------------------- */
  /* MODAL STACKING SUPPORT */
  /* -------------------------------- */
  useEffect(() => {
    modalStackCount++;
    document.body.style.overflow = "hidden";

    return () => {
      modalStackCount--;
      if (modalStackCount === 0) {
        document.body.style.overflow = "";
      }
    };
  }, []);

  const baseZ = 50 + modalStackCount * 10;
  const titleId = useId();

  /* -------------------------------- */
  /* FOCUS: move into the dialog, hand it back on close */
  /* -------------------------------- */
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    // An autofocused field inside wins; otherwise the dialog itself takes focus.
    if (!dialogRef.current?.contains(document.activeElement))
      dialogRef.current?.focus();
    return () => opener?.focus?.();
  }, []);

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 flex items-end justify-center bg-black/40 backdrop-blur-sm md:items-center md:p-4"
        style={{ zIndex: baseZ }}
      >
        <m.div
          ref={dialogRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          initial={isMobile ? { y: "100%" } : { scale: 0.96, opacity: 0 }}
          animate={isMobile ? { y: 0 } : { scale: 1, opacity: 1 }}
          exit={isMobile ? { y: "100%" } : { scale: 0.96, opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className={`
            w-full
            ${isMobile ? "rounded-t-2xl" : `${maxWidthClassName} rounded-2xl`}
            max-h-[90vh]
            bg-[rgb(var(--card))]
            shadow-2xl
            border border-[rgb(var(--border))]
            flex flex-col
            overflow-hidden
            focus:outline-none
          `}
        >
          {/* ===================================== */}
          {/* HEADER — quiet: the form is the subject, not the chrome. The accent
              band stays on ConfirmModal, where its colour says how serious the
              question is. */}
          {/* ===================================== */}
          <div
            className="
              flex items-center justify-between gap-3
              px-4 py-3 sm:px-6 sm:py-4
              border-b border-[rgb(var(--border))]
              bg-[rgb(var(--card))]
              text-[rgb(var(--text-primary))]
            "
          >
            <h2 id={titleId} className="min-w-0 truncate text-base font-semibold sm:text-lg">
              {title}
            </h2>

            <button
              onClick={onClose}
              className="
                p-2 rounded-lg
                text-[rgb(var(--text-muted))]
                hover:bg-[rgb(var(--card-muted))]
                hover:text-[rgb(var(--text-primary))]
                transition-colors
                focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--text-accent))]
              "
              aria-label={t("close")}
              title={`${t("close")} (Esc)`}
            >
              <X size={18} />
            </button>
          </div>

          {/* ===================================== */}
          {/* SCROLLABLE BODY */}
          {/* ===================================== */}
          {/* px-6 to match the header band: at px-5 the body content sat one
              step left of the title, so the dialog had no single left edge. */}
          <div
            className="
              flex-1
              px-4 py-4 sm:px-6 sm:py-5
              space-y-4
              overflow-y-auto overflow-x-visible
          "
          >
            {children}
          </div>
        </m.div>
      </div>
    </AnimatePresence>
  );
}
